<template>
  <div class="permission-app">
  <div class="permission-users-view bg-slate-50">
    <permission-page-header
      icon="mdi-shield-account-outline"
      variant="indigo"
      :title="$t('permission_users_page_title')"
      :subtitle="$t('permission_users_page_desc')"
      :stats="headerStats"
    >
      <template #actions>
        <!-- Divider：stats（資訊）vs actions（操作） -->
        <div v-if="selectedUser" class="w-px h-8 bg-slate-200 mx-1" aria-hidden="true"></div>
        <!-- Unsaved pill（status） -->
        <span
          v-if="selectedUser && dirty"
          class="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200"
          role="status"
          aria-live="polite"
        >
          <span class="w-1.5 h-1.5 rounded-full bg-amber-500 motion-safe:animate-pulse" aria-hidden="true" />
          {{ $t('permission_unsaved') }}
        </span>
        <!-- Save（primary CTA） -->
        <button
          v-if="selectedUser"
          type="button"
          :disabled="!dirty || saving"
          :title="$t('permission_save_changes')"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 cursor-pointer bg-indigo-500 text-white hover:bg-indigo-600 shadow-sm hover:shadow disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed disabled:shadow-none"
          :class="{ 'ring-2 ring-indigo-300 ring-offset-1': dirty && !saving }"
          @click="saveChanges"
        >
          <v-icon size="14" color="white">
            {{ saving ? 'mdi-loading mdi-spin' : 'mdi-content-save-outline' }}
          </v-icon>
          {{ saving ? $t('permission_saving') : $t('permission_save_changes') }}
        </button>
        <!-- Reset（danger action） -->
        <button
          v-if="selectedUser"
          type="button"
          :disabled="resetting"
          :title="$t('permission_reset_tooltip')"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors duration-200 cursor-pointer bg-white border border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-60 disabled:cursor-wait"
          @click="onResetAll"
        >
          <v-icon size="14" color="#e11d48">
            {{ resetting ? 'mdi-loading mdi-spin' : 'mdi-restart' }}
          </v-icon>
          {{ $t('permission_reset_all') }}
        </button>
        <!-- Divider：primary actions vs view options -->
        <div v-if="selectedUser" class="w-px h-8 bg-slate-200 mx-1" aria-hidden="true"></div>
        <!-- Hide legacy toggle -->
        <button
          v-if="selectedUser"
          type="button"
          class="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium border transition-colors duration-200 cursor-pointer"
          :class="hideLegacy
            ? 'bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600'
            : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'"
          :aria-pressed="hideLegacy"
          @click="hideLegacy = !hideLegacy"
        >
          <v-icon size="12" :color="hideLegacy ? 'white' : '#64748b'">mdi-eye-off-outline</v-icon>
          {{ hideLegacy ? $t('permission_hide_legacy_on') : $t('permission_hide_legacy_off') }}
        </button>
        <!-- Preview toggle -->
        <button
          v-if="selectedUser"
          type="button"
          class="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium border transition-colors duration-200 cursor-pointer"
          :class="previewMode
            ? 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600'
            : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'"
          :aria-pressed="previewMode"
          @click="previewMode = !previewMode"
        >
          <v-icon size="12" :color="previewMode ? 'white' : '#64748b'">mdi-eye-outline</v-icon>
          {{ previewMode ? $t('permission_preview_mode_on') : $t('permission_preview_mode_off') }}
        </button>
        <!-- Help popover（共用組件） -->
        <permission-help-popover v-if="selectedUser" mode="user" />
      </template>
    </permission-page-header>
    <permission-sub-tabs />

    <!-- Body: 2-col split；左欄 sticky 兩 panel，右欄 matrix only -->
    <div class="p-4 grid grid-cols-12 gap-4 items-start">
      <!-- 左欄：sticky 清單 + 基本資料（aside 限高，list 填剩餘空間） -->
      <aside class="col-span-12 md:col-span-4 lg:col-span-3 sticky top-4 self-start flex flex-col gap-4 max-h-[calc(100vh-6rem)]">
        <user-list-panel
          class="min-h-0 flex-1"
          :users="users"
          :selected-id="selectedId"
          @select="selectUser"
        />
        <user-basic-info-panel
          class="shrink-0"
          v-if="selectedUser"
          :user="selectedUser"
          :departments="departments"
          :groups="groups"
          :primary-department-id="primaryDepartmentId"
          :user-group-ids="userGroupIds"
          :saving="assignmentSaving"
          @change-department="onChangeDepartment"
          @change-groups="onChangeGroups"
        />
      </aside>

      <!-- 右欄：matrix only -->
      <div class="col-span-12 md:col-span-8 lg:col-span-9 min-w-0">
        <user-permission-matrix
          v-if="selectedUser"
          :user="selectedUser"
          :resources="resources"
          :effective-by-cell="effectiveByCell"
          :overrides="overrides"
          :primary-department-name="primaryDepartmentName"
          :user-group-names="userGroupNames"
          :loading="loadingPermissions"
          :preview-mode="previewMode"
          :hide-legacy-resources="hideLegacy"
          @change-mode="onChangeMode"
          @change-scope="onChangeScope"
        />
        <div
          v-else
          class="bg-white rounded-2xl border border-slate-200 shadow-sm p-20 text-center"
        >
          <v-icon size="64" color="#cbd5e1">mdi-account-circle-outline</v-icon>
          <div class="text-base text-slate-400 mt-4">
            {{ $t('permission_select_user_left') }}
          </div>
        </div>
      </div>
    </div>

    <v-dialog v-model="resetConfirm.show" max-width="440" persistent>
      <v-card class="rounded-xl">
        <v-card-title class="text-base font-bold">
          {{ $t('permission_reset_confirm_title') }}
        </v-card-title>
        <v-card-text class="text-sm text-slate-600">
          {{ $t('permission_reset_confirm_text') }}
        </v-card-text>
        <v-card-actions class="px-5 pb-4">
          <v-spacer />
          <v-btn text :disabled="resetting" @click="resetConfirm.show = false">
            {{ $t('common_cancel') }}
          </v-btn>
          <v-btn color="error" :loading="resetting" :disabled="resetting" @click="performReset">
            {{ $t('permission_reset_all') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 切換使用者確認（dirty 時）-->
    <v-dialog v-model="switchConfirm.show" max-width="420" persistent>
      <v-card class="rounded-xl">
        <v-card-title class="text-base font-bold">
          {{ $t('permission_discard_title') }}
        </v-card-title>
        <v-card-text class="text-sm text-slate-600">
          {{ $t('permission_discard_confirm') }}
        </v-card-text>
        <v-card-actions class="px-5 pb-4">
          <v-spacer />
          <v-btn text @click="switchConfirm.show = false">{{ $t('common_cancel') }}</v-btn>
          <v-btn color="error" @click="confirmSwitchUser">{{ $t('permission_discard_confirm_ok') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="2500" role="alert" aria-live="polite">
      {{ snackbar.text }}
    </v-snackbar>
  </div>
  </div>
</template>

<script>
import {
  HttpUser,
  HttpDepartment,
  HttpGroup,
  HttpResource,
  HttpPermission,
  HttpUserAssignment
} from '@/js/http-Manager'
import {
  DEPENDENT_ACTIONS,
  OVERRIDE_MODE,
  OVERRIDE_MODE_CODE,
  supportsAction
} from '@/js/permission-helpers'
import PermissionPageHeader from '@/components/Permission/PermissionPageHeader'
import PermissionSubTabs from '@/components/Permission/PermissionSubTabs'
import PermissionHelpPopover from '@/components/Permission/PermissionHelpPopover'
import UserListPanel from '@/components/Permission/UserListPanel'
import UserBasicInfoPanel from '@/components/Permission/UserBasicInfoPanel'
import UserPermissionMatrix from '@/components/Permission/UserPermissionMatrix'

export default {
  name: 'PermissionUsersView',
  components: {
    PermissionPageHeader,
    PermissionSubTabs,
    PermissionHelpPopover,
    UserListPanel,
    UserBasicInfoPanel,
    UserPermissionMatrix
  },
  data () {
    return {
      users: [],
      departments: [],
      groups: [],
      resources: [],
      selectedId: null,
      // 指派（部門 / 群組）走 instant save + quiet reload（onChange 直接 PUT 後只刷 effectiveByCell）
      primaryDepartmentId: null,
      userGroupIds: [],
      // override cell 走 draft+save；savedOverrides 是 baseline 用於 diff
      overrides: {},
      savedOverrides: {},
      // 獨立 flag：指派儲存 / 覆寫儲存 / 重設，避免互相干擾 disable 狀態
      assignmentSaving: false,
      saving: false,
      resetting: false,
      previewMode: false,
      hideLegacy: (() => {
        try { const v = window.localStorage.getItem('permission_users_hide_legacy'); return v === null ? true : v === '1' } catch (e) { return true }
      })(),
      effectiveByCell: {},
      loadingPermissions: false,
      snackbar: { show: false, text: '', color: 'success' },
      resetConfirm: { show: false },
      switchConfirm: { show: false, targetUserId: null }
    }
  },
  computed: {
    selectedUser () {
      return this.users.find(u => u.Id === this.selectedId) || null
    },
    primaryDepartmentName () {
      const d = this.departments.find(x => x.id === this.primaryDepartmentId)
      return d ? d.name : ''
    },
    userGroupNames () {
      return this.userGroupIds
        .map(id => this.groups.find(g => g.id === id))
        .filter(Boolean)
        .map(g => g.name)
    },
    headerStats () {
      return [
        { label: this.$t('permission_users_title_short'), value: this.users.length, icon: 'mdi-account-multiple-outline' },
        { label: this.$t('permission_dept_short'), value: this.departments.length, icon: 'mdi-domain' },
        { label: this.$t('permission_group_short'), value: this.groups.length, icon: 'mdi-account-group-outline' }
      ]
    },
    // dirty：只看 override（指派是 instant save，沒有 draft）
    overrideDirty () {
      const { upserts, deletes } = this.diffOverrides()
      return upserts.length > 0 || deletes.length > 0
    },
    dirty () {
      return this.overrideDirty
    }
  },
  watch: {
    hideLegacy (val) {
      try { window.localStorage.setItem('permission_users_hide_legacy', val ? '1' : '0') } catch (e) { /* ignore quota / private mode */ }
    }
  },
  async mounted () {
    await this.loadAll()
  },
  methods: {
    async loadAll () {
      try {
        const [uRes, dRes, gRes, rRes] = await Promise.all([
          HttpUser.GetUsers(),
          HttpDepartment.GetAll(true),
          HttpGroup.GetAll(true),
          HttpResource.GetAll()
        ])
        if (uRes && uRes.status === 200) {
          const data = uRes.data && uRes.data.Data ? uRes.data.Data : uRes.data
          this.users = Array.isArray(data) ? data : []
        }
        if (dRes && dRes.status === 200) this.departments = dRes.data || []
        if (gRes && gRes.status === 200) this.groups = gRes.data || []
        if (rRes && rRes.status === 200) this.resources = rRes.data || []
        if (this.users.length > 0) this.selectUser(this.users[0].Id)
      } catch (e) {
        this.showSnackbar('error', this.$t('load_fail'))
      }
    },
    async selectUser (id) {
      if (id === this.selectedId) return
      if (this.dirty) {
        // 樣式一致：用 v-dialog 而非 window.confirm（避免瀏覽器原生框破壞設計語言）
        this.switchConfirm = { show: true, targetUserId: id }
        return
      }
      await this.doSelectUser(id)
    },
    async doSelectUser (id) {
      this.selectedId = id
      await Promise.all([this.loadAssignments(), this.loadPermissions()])
    },
    async confirmSwitchUser () {
      const target = this.switchConfirm.targetUserId
      this.switchConfirm = { show: false, targetUserId: null }
      if (target) await this.doSelectUser(target)
    },
    async loadAssignments () {
      if (!this.selectedId) return
      try {
        const [deptRes, grpRes] = await Promise.all([
          HttpUserAssignment.GetDepartments(this.selectedId),
          HttpUserAssignment.GetGroups(this.selectedId)
        ])
        const deptList = (deptRes && deptRes.status === 200) ? deptRes.data : []
        const primary = deptList.find(d => d.isPrimary) || deptList[0]
        this.primaryDepartmentId = primary ? primary.departmentId : null
        const grpList = (grpRes && grpRes.status === 200) ? grpRes.data : []
        this.userGroupIds = Array.isArray(grpList) ? [...grpList] : []
      } catch (e) {
        console.error('loadAssignments failed', e)
      }
    },
    // Quiet reload：只刷 effectiveByCell（切換部門/群組後讓 matrix 立刻反映新繼承值），
    // 不動 overrides/savedOverrides，避免清掉使用者正在編輯但未儲存的 draft cell；
    // 也不設 loadingPermissions → matrix 保留舊畫面直到新資料 swap，減少閃爍
    async reloadEffectiveQuietly () {
      if (!this.selectedId) return
      try {
        const effRes = await HttpPermission.GetEffectivePermissions(this.selectedId)
        const effList = (effRes && effRes.status === 200) ? effRes.data : []
        const byCell = {}
        for (const e of effList) byCell[`${e.resourceKey}:${e.action}`] = e
        this.effectiveByCell = byCell
      } catch (e) {
        console.error('reloadEffectiveQuietly failed', e)
      }
    },
    async loadPermissions () {
      if (!this.selectedId) return
      this.loadingPermissions = true
      try {
        const [effRes, ovRes] = await Promise.all([
          HttpPermission.GetEffectivePermissions(this.selectedId),
          HttpPermission.GetUserOverrides(this.selectedId)
        ])
        const effList = (effRes && effRes.status === 200) ? effRes.data : []
        const byCell = {}
        for (const e of effList) {
          byCell[`${e.resourceKey}:${e.action}`] = e
        }
        this.effectiveByCell = byCell

        const ovList = (ovRes && ovRes.status === 200) ? ovRes.data : []
        const ovMap = {}
        for (const o of ovList) {
          if (!ovMap[o.resourceKey]) ovMap[o.resourceKey] = {}
          const mode = o.overrideMode === OVERRIDE_MODE_CODE.GRANT
            ? OVERRIDE_MODE.GRANT
            : OVERRIDE_MODE.REVOKE
          ovMap[o.resourceKey][o.action] = { mode, scope: o.scope }
        }
        this.overrides = ovMap
        this.savedOverrides = JSON.parse(JSON.stringify(ovMap))
      } catch (e) {
        const msg = (e && e.message) ? e.message : this.$t('load_fail')
        this.showSnackbar('error', msg)
      } finally {
        this.loadingPermissions = false
      }
    },
    // 共用 instant save runner：optimistic update → API → fail rollback + snackbar / success quiet reload
    // rollback 後也 quiet reload：避免後端已寫入但網路回傳錯誤（例如 HTTP 500 但 DB 已 commit）時 UI 停在舊 state
    async _runAssignmentSave ({ optimistic, rollback, apiCall }) {
      if (this.assignmentSaving || !this.selectedId) return
      this.assignmentSaving = true
      try {
        optimistic()
        const res = await apiCall()
        if (!res || res.status !== 200) {
          rollback()
          const msg = res && res.data && res.data.message
          this.showSnackbar('error', msg || this.$t('save_fail'))
          await this.reloadEffectiveQuietly()
          return
        }
        await this.reloadEffectiveQuietly()
      } catch (e) {
        rollback()
        this.showSnackbar('error', (e && e.message) || this.$t('save_fail'))
        await this.reloadEffectiveQuietly()
      } finally {
        this.assignmentSaving = false
      }
    },
    async onChangeDepartment (newDeptId) {
      const prev = this.primaryDepartmentId
      await this._runAssignmentSave({
        optimistic: () => { this.primaryDepartmentId = newDeptId },
        rollback: () => { this.primaryDepartmentId = prev },
        apiCall: () => HttpUserAssignment.SetDepartments(
          this.selectedId,
          newDeptId ? [{ departmentId: newDeptId, isPrimary: true }] : []
        )
      })
    },
    async onChangeGroups (newGroupIds) {
      const prev = [...this.userGroupIds]
      await this._runAssignmentSave({
        optimistic: () => { this.userGroupIds = [...newGroupIds] },
        rollback: () => { this.userGroupIds = prev },
        apiCall: () => HttpUserAssignment.SetGroups(this.selectedId, newGroupIds)
      })
    },
    onResetAll () {
      this.resetConfirm.show = true
    },
    async performReset () {
      if (!this.selectedId) return
      this.resetting = true
      try {
        const res = await HttpPermission.ClearAllUserOverrides(this.selectedId)
        const ok = res && (res.status === 204 || res.status === 200)
        if (ok) {
          this.showSnackbar('success', this.$t('permission_reset_success'))
          this.resetConfirm.show = false
          await this.loadPermissions()
        } else {
          const msg = res && res.data && res.data.message
          this.showSnackbar('error', msg || this.$t('permission_reset_fail'))
        }
      } catch (e) {
        this.showSnackbar('error', this.$t('permission_reset_fail'))
      } finally {
        this.resetting = false
      }
    },
    modeOf (resourceKey, action) {
      const ov = this.overrides[resourceKey] && this.overrides[resourceKey][action]
      return ov ? ov.mode : OVERRIDE_MODE.INHERIT
    },
    setDraftCell (resourceKey, action, mode, scope) {
      // Vue 2 需要 $set 觸發 reactivity（巢狀物件 key 新增時）
      if (mode === OVERRIDE_MODE.INHERIT) {
        if (this.overrides[resourceKey]) {
          this.$delete(this.overrides[resourceKey], action)
          if (Object.keys(this.overrides[resourceKey]).length === 0) {
            this.$delete(this.overrides, resourceKey)
          }
        }
        return
      }
      if (!this.overrides[resourceKey]) {
        this.$set(this.overrides, resourceKey, {})
      }
      const existingScope = (this.overrides[resourceKey][action] || {}).scope
      const effScope = (this.effectiveByCell[`${resourceKey}:${action}`] || {}).scope
      const nextScope = scope !== undefined ? scope : (existingScope !== undefined ? existingScope : effScope)
      this.$set(this.overrides[resourceKey], action, { mode, scope: nextScope != null ? nextScope : null })
    },
    onChangeMode ({ resource, action, mode }) {
      const prev = this.modeOf(resource.key, action)
      if (prev === mode) return
      this.setDraftCell(resource.key, action, mode)
      // 三態連動規則在 draft 層套用（改 else-if 互斥；避免未來新增分支時意外 fall-through）
      if (mode === OVERRIDE_MODE.GRANT && action !== 'view' && supportsAction(resource, 'view')) {
        // Grant 非 view → 同 resource 的 view 也 Grant
        if (this.modeOf(resource.key, 'view') !== OVERRIDE_MODE.GRANT) {
          this.setDraftCell(resource.key, 'view', OVERRIDE_MODE.GRANT)
        }
      } else if (mode === OVERRIDE_MODE.REVOKE && action === 'view') {
        // Revoke view → 同 resource 所有 CUD/export/approve 都 Revoke
        for (const dep of DEPENDENT_ACTIONS) {
          if (supportsAction(resource, dep) && this.modeOf(resource.key, dep) !== OVERRIDE_MODE.REVOKE) {
            this.setDraftCell(resource.key, dep, OVERRIDE_MODE.REVOKE)
          }
        }
      } else if (mode === OVERRIDE_MODE.INHERIT && action === 'view') {
        // 反向 cascade：view 改回 INHERIT → 清除 CUD 的 REVOKE draft
        // 接受「無法區分 user-intentional REVOKE」的設計選擇（由 SpecFlow 鎖定此行為）
        for (const dep of DEPENDENT_ACTIONS) {
          if (this.modeOf(resource.key, dep) === OVERRIDE_MODE.REVOKE) {
            this.setDraftCell(resource.key, dep, OVERRIDE_MODE.INHERIT)
          }
        }
      }
    },
    onChangeScope ({ resource, action, scope }) {
      // 改 scope 視為明確 grant + 指定範圍（draft 層，儲存才生效）
      this.setDraftCell(resource.key, action, OVERRIDE_MODE.GRANT, scope)
    },
    diffOverrides () {
      // 比對 draft vs saved，產出 { upserts: [{resourceKey, action, mode, scope}], deletes: [{resourceKey, action}] }
      const keys = new Set([
        ...Object.keys(this.overrides),
        ...Object.keys(this.savedOverrides)
      ])
      const upserts = []
      const deletes = []
      for (const rk of keys) {
        const draftRes = this.overrides[rk] || {}
        const savedRes = this.savedOverrides[rk] || {}
        const actions = new Set([...Object.keys(draftRes), ...Object.keys(savedRes)])
        for (const a of actions) {
          const d = draftRes[a]
          const s = savedRes[a]
          if (!d && s) {
            deletes.push({ resourceKey: rk, action: a })
          } else if (d && (!s || s.mode !== d.mode || s.scope !== d.scope)) {
            upserts.push({ resourceKey: rk, action: a, mode: d.mode, scope: d.scope })
          }
        }
      }
      return { upserts, deletes }
    },
    async saveChanges () {
      // 只處理 override（指派已是 instant save）。平行呼叫 + 錯誤收集；失敗 reload baseline 讓 diff 維持 pending
      if (!this.selectedId || this.saving) return
      this.saving = true
      const errors = []
      const succeeded = []
      try {
        // 按 resource 分組：同 resource 的操作序列化（防後端 cascade race），跨 resource 平行
        const { upserts, deletes } = this.diffOverrides()
        const byResource = new Map()
        for (const d of deletes) {
          if (!byResource.has(d.resourceKey)) byResource.set(d.resourceKey, [])
          byResource.get(d.resourceKey).push({ kind: 'delete', ...d })
        }
        for (const u of upserts) {
          if (!byResource.has(u.resourceKey)) byResource.set(u.resourceKey, [])
          byResource.get(u.resourceKey).push({ kind: 'upsert', ...u })
        }
        const processOp = async (op) => {
          const tag = `${op.resourceKey}:${op.action}`
          try {
            if (op.kind === 'delete') {
              const res = await HttpPermission.DeleteUserOverride(this.selectedId, op.resourceKey, op.action)
              if (res && (res.status === 204 || res.status === 200)) succeeded.push(tag)
              else errors.push(`覆寫刪除 ${tag}：HTTP ${res ? res.status : 'n/a'}`)
            } else {
              const overrideMode = op.mode === OVERRIDE_MODE.GRANT ? OVERRIDE_MODE_CODE.GRANT : OVERRIDE_MODE_CODE.REVOKE
              const res = await HttpPermission.UpsertUserOverride(this.selectedId, {
                resourceKey: op.resourceKey, action: op.action, overrideMode, scope: op.scope
              })
              if (res && res.status === 200) succeeded.push(tag)
              else errors.push(`覆寫 ${tag}：HTTP ${res ? res.status : 'n/a'}`)
            }
          } catch (e) {
            errors.push(`${op.kind === 'delete' ? '覆寫刪除' : '覆寫'} ${tag}：${(e && e.message) || 'network error'}`)
          }
        }
        const resourceTasks = [...byResource.values()].map(async (ops) => {
          for (const op of ops) await processOp(op)
        })
        await Promise.all(resourceTasks)

        if (errors.length > 0) {
          // 精準分流：全失敗用 partial_fail；有成功 + 失敗用 partial_success（讓使用者知道哪些同步了）
          const errs = errors.slice(0, 2).join('; ')
          const msg = succeeded.length > 0
            ? this.$t('permission_save_partial_success', { success: succeeded.length, fail: errors.length, errors: errs })
            : this.$t('permission_save_partial_fail', { errors: errs })
          this.showSnackbar('error', msg)
        } else {
          this.showSnackbar('success', this.$t('save_success'))
        }
      } catch (e) {
        this.showSnackbar('error', (e && e.message) || this.$t('save_fail'))
      } finally {
        // reload overrides baseline；失敗的會繼續在 diff 中保留 pending 狀態
        // 包 try/catch：loadPermissions 若拋出（例如 API 503），saving 仍須設回 false，避免按鈕永久 disabled
        try {
          await this.loadPermissions()
        } catch (reloadErr) {
          console.error('reloadPermissions after save failed', reloadErr)
        }
        this.saving = false
      }
    },
    showSnackbar (color, text) {
      this.snackbar = { show: true, color, text }
    }
  }
}
</script>

<style scoped>
.permission-users-view {
  margin: -20px;
  min-height: calc(100vh - 64px);
  padding-bottom: 24px;
}
</style>
